import { createAsyncStorage } from '@react-native-async-storage/async-storage';

import { IKeyValueStorage } from '@utils/storage';

const storage = createAsyncStorage('tasktag');

// One-time migration from v2 (keys stored raw in localStorage) to v3
// (namespaced database). Only the auth/session keys matter for preventing
// logout; other keys (tutorial state, search history) are non-critical.
const MIGRATION_SENTINEL = '__tasktag_v3_migrated__';
const MIGRATE_KEYS = ['authToken', 'keepSignedIn', 'cacheClearVersion'];

const migrationDone: Promise<void> = (async () => {
  if (
    typeof localStorage === 'undefined' ||
    localStorage.getItem(MIGRATION_SENTINEL)
  ) {
    return;
  }
  try {
    for (const key of MIGRATE_KEYS) {
      const legacy = localStorage.getItem(key);
      if (legacy !== null) {
        await storage.setItem(key, legacy);
      }
    }
    localStorage.setItem(MIGRATION_SENTINEL, '1');
  } catch {
    // Non-fatal: app falls back to a fresh session if migration fails
  }
})();

export const AsyncStorage: IKeyValueStorage = {
  // Await migration before the first reads so auth state is never empty
  // due to a mid-migration race.
  getItem: async (key) => {
    await migrationDone;
    return storage.getItem(key);
  },
  setItem: (key, value) => storage.setItem(key, value),
  deleteItem: (key) => storage.removeItem(key),
  multiSet: async (keyValuePairs) => {
    for (const [key, value] of keyValuePairs) {
      await storage.setItem(key, value);
    }
  },
  multiGet: (keys) =>
    Promise.all(
      keys.map(
        async (key): Promise<readonly [string, string | null]> => [
          key,
          await storage.getItem(key),
        ]
      )
    ),
  multiRemove: async (keys) => {
    for (const key of keys) {
      await storage.removeItem(key);
    }
  },
  clear: () => storage.clear(),
};

export const SecurityStorage: IKeyValueStorage = AsyncStorage;
